import { Fragment, useRef } from 'react';
import {
  DrawerTooltipContainer,
  DrawerTooltipContainerRef,
} from 'src/v2/commonComponents/DrawerTooltipContainer';
import { DownloadFileTrigger } from '../DownloadFileTrigger';
import styles from './DownloadFileTool.module.scss';
import { DownloadFileButton } from '../DownloadFileButton';
import { ConversionTargetType, Message } from 'src/types';
import { useDownloadFileTool, useUniqueId } from 'src/hooks';
import { TextDivider } from 'src/v2/commonComponents/TextDivider';
import { toast } from 'react-toastify';
import { ArrowClockwise } from '@phosphor-icons/react';
import { SVG_SIZE_L } from 'src/constants';
import { useChatHistory } from 'src/hooks';

type ToolType = 'message' | 'editor';

interface DownloadFileToolProps {
  toolType: ToolType;
  message?: Message;
  activeTab?: string;
  disabled?: boolean;
  temporarySkipPDF?: boolean;
  taskTitle?: string;
}

export const DownloadFileTool = ({
  toolType,
  message,
  activeTab,
  disabled,
  temporarySkipPDF,
  taskTitle,
}: DownloadFileToolProps) => {
  const { isNinjaRetired } = useChatHistory();
  const drawerRef = useRef<DrawerTooltipContainerRef>(null);

  const { uniqueId } = useUniqueId();
  const TOOLTIP_ID = `download-file-anchor-${uniqueId}`;

  const DOWNLOAD_OPTIONS_LIST = [
    {
      title: 'Microsoft Document',
      fileType: ConversionTargetType.DOCX,
      displayedFormat: ConversionTargetType.DOCX,
      hidden: false,
      isNew: false,
      hasDividerAfter: false,
    },
    {
      title: 'PDF Document',
      fileType: ConversionTargetType.PDF,
      displayedFormat: ConversionTargetType.PDF,
      hidden: temporarySkipPDF,
      isNew: false,
      hasDividerAfter: false,
    },
  ];

  const { onDownload, isLoading } = useDownloadFileTool({
    message,
    activeTab: activeTab || '',
    taskTitle,
    downloadToolType: toolType,
  });

  const showProgressToast = (fileType: ConversionTargetType) => {
    const documentData = DOWNLOAD_OPTIONS_LIST.find(
      (item) => item.fileType === fileType,
    );
    const documentTitle = documentData?.title || '';
    const displayedFormat = documentData?.displayedFormat || fileType;

    const toastText = `Creating ${documentTitle} (.${displayedFormat})`;

    toast.loading(toastText, {
      icon: <ArrowClockwise className={styles.toastIcon} size={SVG_SIZE_L} />,
      isLoading,
    });
  };

  const handleBeforeFunc = (fileType: ConversionTargetType) => {
    showProgressToast(fileType);
    drawerRef.current?.onVisibilityToggle();
  };

  const handleDownloadClick = (fileType: ConversionTargetType) => {
    onDownload({
      fileType,
      onStart: () => handleBeforeFunc(fileType),
    });
  };

  if (isNinjaRetired) {
    return null;
  }

  if (!activeTab && toolType === 'message') {
    return null;
  }

  return (
    <DrawerTooltipContainer<{ toolType: ToolType }>
      ref={drawerRef}
      onlyTooltip
      id={TOOLTIP_ID}
      ToggleButton={DownloadFileTrigger}
      toggleButtonProps={{ toolType }}
      place="top"
      disabledToggle={disabled || isLoading}
      classNameTooltip={styles.tooltip}
    >
      <div className={styles.root}>
        {DOWNLOAD_OPTIONS_LIST.map(
          ({
            title,
            fileType,
            hidden,
            isNew,
            displayedFormat,
            hasDividerAfter,
          }) =>
            hidden ? null : (
              <Fragment key={fileType}>
                <DownloadFileButton
                  key={fileType}
                  title={title}
                  onClick={() => handleDownloadClick(fileType)}
                  disabled={isLoading}
                  isNew={isNew}
                  displayedFormat={displayedFormat}
                />
                {hasDividerAfter && <TextDivider />}
              </Fragment>
            ),
        )}
      </div>
    </DrawerTooltipContainer>
  );
};
